/* eslint-disable react/no-unknown-property */
import {
  Button,
  Icon,
  IconButton,
  TextCaption,
} from '@pypestream/design-system';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { passAt } from '../../utils/evaluation-rubric';
import {
  AgentSelect,
  ChannelSelect,
  PanelHead,
  StartSegments,
} from './validate-panels';
import {
  START_POSITIONS,
  relativeTime,
  type ExpectedAgent,
  type RunChannel,
  type ValidateRun,
} from './validate-runs';
import {
  MAX_CONCURRENCY,
  MAX_ITERATIONS,
  MAX_MODELS,
  MAX_TURNS_LIMIT,
  caseIsScripted,
  clampConcurrency,
  clampIterations,
  clampMaxTurns,
  expandSuite,
  newCase,
  runsOfSuiteRun,
  suiteBlocker,
  suiteModels,
  summarizeSuiteRun,
  type SuiteCase,
  type SuiteContext,
  type SuiteRun,
  type ValidateSuite,
} from './validate-suites';
import { useExpectedAgentEnabled } from './validate-agents';
import { describeStep, parseScript, patternLines } from './case-script';
import { describeSchedule, type SuiteSchedule } from './validate-schedules';
import {
  casesFromCsv,
  casesToCsv,
  rollupByModel,
  suiteRunToJunit,
  suiteToJson,
} from './suite-io';

/**
 * Suites: the testing tool's saved test suites, inside Validate. The list and
 * each suite's latest run live in the main column; the editor and "Save as
 * suite case" open in the right-hand panel, like Test and New scenario.
 */

export interface SuiteProgress {
  suiteRunId: string;
  suiteId: string;
  done: number;
  total: number;
}

/** Save `text` as a file, the browser's way. */
const download = (name: string, text: string, type: string) => {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** "Orders & returns" → "orders-returns". */
const fileStem = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'suite';

/* ---- Suite run summary -------------------------------------------------- */

const SuiteRunSummaryCard: FC<{
  suiteRun: SuiteRun;
  runs: ValidateRun[];
  progress: SuiteProgress | null;
  now: number;
  onOpenRun: (runId: string) => void;
  onStop: () => void;
}> = ({ suiteRun, runs, progress, now, onOpenRun, onStop }) => {
  const own = runsOfSuiteRun(suiteRun, runs);
  const s = summarizeSuiteRun(own);
  const models = [
    ...new Set(own.map((r) => r.model).filter((m): m is string => !!m)),
  ];
  const byModel = models.length ? rollupByModel(own, models) : [];
  const running = suiteRun.status === 'running' && !!progress;
  const done = running ? progress.done : own.length;
  const share = suiteRun.total ? (done / suiteRun.total) * 100 : 0;

  return (
    <div
      className="c-validate-suite-run"
      test-id="bobcat-validate-suite-run-summary"
    >
      <div className="c-validate-suite-run__head">
        <span>
          {running
            ? `Running on ${suiteRun.channelName} · ${done} of ${suiteRun.total} done`
            : `Last run ${relativeTime(suiteRun.startedAt, now)} on ${suiteRun.channelName}${
                suiteRun.status === 'stopped' ? ' · stopped' : ''
              }`}
        </span>
        {running ? (
          <Button
            size="small"
            variant="ghost"
            test-id="bobcat-validate-suite-stop"
            onClick={onStop}
          >
            Stop
          </Button>
        ) : (
          own.length > 0 && (
            <Button
              size="small"
              variant="ghost"
              test-id="bobcat-validate-suite-junit"
              onClick={() =>
                download(
                  `${fileStem(suiteRun.suiteName)}-results.xml`,
                  suiteRunToJunit(suiteRun, own),
                  'application/xml'
                )
              }
            >
              <Icon name="download" slot="prefix" size="xsmall" />
              JUnit
            </Button>
          )
        )}
      </div>
      {running && (
        <div className="c-validate-meter c-validate-suite-run__progress">
          <i style={{ width: `${share}%` }} />
        </div>
      )}
      <div className="c-validate-suite-run__stats">
        <span>
          <b>{s.passRate === null ? '—' : `${s.passRate}%`}</b> passed
          {s.scored ? ` (${s.passed} of ${s.scored})` : ''}
        </span>
        <span>
          <b>{s.qualityAvg ?? '—'}</b> avg quality
        </span>
        <span>
          <b>{s.failed + s.errored}</b>{' '}
          {s.failed + s.errored === 1 ? 'failure' : 'failures'}
        </span>
      </div>
      {byModel.length > 0 && (
        <table
          className="c-validate-suite-models"
          test-id="bobcat-validate-suite-models"
        >
          <thead>
            <tr>
              <th>Model</th>
              <th>Passed</th>
              <th>Avg reply</th>
            </tr>
          </thead>
          <tbody>
            {byModel.map((m) => (
              <tr key={m.model}>
                <td>{m.model}</td>
                <td>
                  {m.passRate === null
                    ? '—'
                    : `${m.passRate}% (${m.passed} of ${m.scored})`}
                </td>
                <td>
                  {m.responseMs === null
                    ? '—'
                    : `${(m.responseMs / 1000).toFixed(2)}s`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {own.length > 0 && (
        <ul className="c-validate-suite-run__runs">
          {own.map((r) => {
            const failed = s.failures.includes(r);
            return (
              <li key={r.id}>
                <button
                  type="button"
                  className={`c-validate-suite-run__run ${failed ? 'is-fail' : 'is-pass'}`}
                  test-id="bobcat-validate-suite-run-link"
                  onClick={() => onOpenRun(r.id)}
                >
                  <i />
                  <span>
                    {r.suite?.caseName || r.title}
                    {r.suite && r.suite.iteration > 1
                      ? ` · run ${r.suite.iteration}`
                      : ''}
                    {r.model ? ` · ${r.model}` : ''}
                  </span>
                  <em>
                    {r.score.status === 'scored'
                      ? failed
                        ? 'Failed'
                        : 'Passed'
                      : r.score.status === 'pending'
                        ? 'Scoring…'
                        : 'Error'}
                  </em>
                  <Icon name="chevron-right" size="xsmall" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/* ---- Suites list -------------------------------------------------------- */

export const SuitesView: FC<{
  suites: ValidateSuite[];
  channels: RunChannel[];
  latestRuns: Map<string, SuiteRun>;
  runs: ValidateRun[];
  progress: SuiteProgress | null;
  now: number;
  onNew: () => void;
  onEdit: (suiteId: string) => void;
  onDuplicate: (suiteId: string) => void;
  onDelete: (suiteId: string) => void;
  onRun: (suiteId: string) => void;
  onStop: () => void;
  onOpenRun: (runId: string) => void;
  /** A suite file (Validate's or the testing tool's JSON, or CSV). Resolves an error, or null. */
  onImport: (file: File) => Promise<string | null>;
  /** The solution's schedules, by suite. */
  schedules?: SuiteSchedule[];
  onSchedule?: (suiteId: string) => void;
}> = ({
  suites,
  channels,
  latestRuns,
  runs,
  progress,
  now,
  onNew,
  onEdit,
  onDuplicate,
  onDelete,
  onRun,
  onStop,
  onOpenRun,
  onImport,
  schedules = [],
  onSchedule,
}) => {
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const importInput = useRef<HTMLInputElement>(null);
  const channelExists = (appId: string) =>
    channels.some((c) => c.appId === appId);

  return (
    <div className="c-validate-suites" test-id="bobcat-validate-suites">
      <div className="c-validate-runs-bar">
        <span className="c-validate-runs-bar__meta">
          A suite is a set of scenarios you run together — every case is scored
          at {passAt()}/100 and lands in Runs.
        </span>
        <input
          ref={importInput}
          type="file"
          accept=".json,.csv,application/json,text/csv"
          hidden
          test-id="bobcat-validate-suite-import-file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void onImport(file).then(setImportError);
          }}
        />
        <Button
          size="small"
          variant="ghost"
          test-id="bobcat-validate-suite-import"
          onClick={() => {
            setImportError(null);
            importInput.current?.click();
          }}
        >
          <Icon name="upload" slot="prefix" size="xsmall" />
          Import
        </Button>
        <Button
          size="small"
          variant="primary"
          test-id="bobcat-validate-new-suite"
          onClick={onNew}
        >
          <Icon name="add" slot="prefix" size="xsmall" />
          New suite
        </Button>
      </div>
      {importError && (
        <p className="c-validate-error" role="alert">
          Couldn’t import that file: {importError}
        </p>
      )}

      {suites.length === 0 ? (
        <div className="c-validate-runs__empty">
          No suites yet. Create one, or open a scenario run and choose{' '}
          <b>Save as suite case</b>.
        </div>
      ) : (
        suites.map((suite) => {
          const blocker = suiteBlocker(suite, channelExists);
          const channel = channels.find((c) => c.appId === suite.channelAppId);
          const tasks = expandSuite(suite).length;
          const latest = latestRuns.get(suite.id);
          const busy = !!progress;
          const thisRunning = progress?.suiteId === suite.id;
          return (
            <div
              key={suite.id}
              className="c-validate-suite"
              test-id="bobcat-validate-suite"
            >
              <div className="c-validate-suite__head">
                <div className="c-validate-suite__title">
                  <b>{suite.name}</b>
                  <span>
                    {suite.cases.length}{' '}
                    {suite.cases.length === 1 ? 'case' : 'cases'}
                    {suite.iterations > 1
                      ? ` × ${suite.iterations}`
                      : ''} ·{' '}
                    {channel
                      ? `${channel.name} · ${channel.env === 'live' ? 'Live' : 'Sandbox'}`
                      : 'No channel'}
                    {` · ${suite.concurrency} at a time`}
                    {suiteModels(suite).length
                      ? ` · comparing ${suiteModels(suite).length} models`
                      : ''}
                  </span>
                </div>
                <div className="c-validate-suite__actions">
                  {confirmDelete === suite.id ? (
                    <>
                      <TextCaption>Delete this suite?</TextCaption>
                      <Button
                        size="small"
                        variant="secondary"
                        test-id="bobcat-validate-suite-delete-confirm"
                        onClick={() => {
                          setConfirmDelete(null);
                          onDelete(suite.id);
                        }}
                      >
                        Delete
                      </Button>
                      <Button
                        size="small"
                        variant="ghost"
                        onClick={() => setConfirmDelete(null)}
                      >
                        Keep
                      </Button>
                    </>
                  ) : (
                    <>
                      <IconButton
                        name="edit"
                        size="small"
                        variant="tertiary"
                        aria-label="Edit suite"
                        test-id="bobcat-validate-suite-edit"
                        disabled={thisRunning}
                        onClick={() => onEdit(suite.id)}
                      />
                      {onSchedule && (
                        <IconButton
                          name="clock"
                          size="small"
                          variant="tertiary"
                          aria-label="Schedule suite"
                          test-id="bobcat-validate-suite-schedule"
                          onClick={() => onSchedule(suite.id)}
                        />
                      )}
                      <IconButton
                        name="duplicate"
                        size="small"
                        variant="tertiary"
                        aria-label="Duplicate suite"
                        test-id="bobcat-validate-suite-duplicate"
                        onClick={() => onDuplicate(suite.id)}
                      />
                      <IconButton
                        name="delete"
                        size="small"
                        variant="tertiary"
                        aria-label="Delete suite"
                        test-id="bobcat-validate-suite-delete"
                        disabled={thisRunning}
                        onClick={() => setConfirmDelete(suite.id)}
                      />
                      <Button
                        size="small"
                        variant="primary"
                        test-id="bobcat-validate-suite-run"
                        disabled={!!blocker || busy}
                        onClick={() => onRun(suite.id)}
                      >
                        <Icon name="play" slot="prefix" size="xsmall" />
                        Run suite
                        {tasks ? ` · ${tasks}` : ''}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {schedules
                .filter((sch) => sch.suiteId === suite.id)
                .map((sch) => (
                  <button
                    key={sch.id}
                    type="button"
                    className="c-validate-suite__schedule"
                    test-id="bobcat-validate-suite-schedule-line"
                    onClick={() => onSchedule?.(suite.id)}
                  >
                    <Icon name="clock" size="xsmall" />
                    {sch.enabled ? describeSchedule(sch) : 'Schedule off'}
                    {sch.enabled && sch.nextRunAt
                      ? ` · next ${new Date(sch.nextRunAt).toLocaleString(
                          undefined,
                          {
                            weekday: 'short',
                            hour: 'numeric',
                            minute: '2-digit',
                          }
                        )}`
                      : ''}
                  </button>
                ))}
              {blocker && !thisRunning && (
                <p className="c-validate-suite__blocker">{blocker}</p>
              )}
              {busy && !thisRunning && !blocker && (
                <p className="c-validate-suite__blocker">
                  Another suite is running.
                </p>
              )}
              {latest && (
                <SuiteRunSummaryCard
                  suiteRun={latest}
                  runs={runs}
                  progress={thisRunning ? progress : null}
                  now={now}
                  onOpenRun={onOpenRun}
                  onStop={onStop}
                />
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

/* ---- Suite editor ------------------------------------------------------- */

/** A list typed one item per line; the text is kept as typed, the list cleaned. */
const LinesField: FC<{
  label: string;
  value: string[] | undefined;
  placeholder: string;
  testId: string;
  rows?: number;
  onChange: (lines: string[] | undefined) => void;
}> = ({ label, value, placeholder, testId, rows = 2, onChange }) => {
  const [text, setText] = useState((value ?? []).join('\n'));
  return (
    <label className="c-validate-field">
      <span>{label}</span>
      <textarea
        rows={rows}
        value={text}
        placeholder={placeholder}
        test-id={testId}
        onChange={(e) => {
          setText(e.target.value);
          const lines = patternLines(e.target.value);
          onChange(lines.length ? lines : undefined);
        }}
      />
    </label>
  );
};

const CaseEditor: FC<{
  index: number;
  value: SuiteCase;
  agents: ExpectedAgent[];
  open: boolean;
  onToggle: () => void;
  onChange: (next: SuiteCase) => void;
  onRemove: () => void;
}> = ({ index, value, agents, open, onToggle, onChange, onRemove }) => {
  const expectedAgentEnabled = useExpectedAgentEnabled();
  const set = (patch: Partial<SuiteCase>) => onChange({ ...value, ...patch });
  const start = START_POSITIONS.find((p) => p.value === value.startAt);
  // Scripted once it has a script, or once Script is picked for an empty one.
  const [scriptMode, setScriptMode] = useState(caseIsScripted(value));
  const parsed = useMemo(
    () => (scriptMode ? parseScript(value.script ?? '') : null),
    [scriptMode, value.script]
  );
  return (
    <div
      className={`c-validate-case ${open ? 'is-open' : ''}`}
      test-id="bobcat-validate-suite-case"
    >
      <div className="c-validate-case__head">
        <button type="button" onClick={onToggle}>
          <span className="c-validate-case__n">{index + 1}</span>
          <span className="c-validate-case__name">
            {value.name.trim() || value.description.trim() || 'New case'}
          </span>
          <Icon name={open ? 'chevron-up' : 'chevron-down'} size="xsmall" />
        </button>
        <IconButton
          name="delete"
          size="small"
          variant="tertiary"
          aria-label="Remove case"
          onClick={onRemove}
        />
      </div>
      {open && (
        <div className="c-validate-case__body">
          <label className="c-validate-field">
            <span>Name</span>
            <input
              value={value.name}
              placeholder="e.g. Incomplete street name"
              test-id="bobcat-validate-case-name"
              onChange={(e) => set({ name: e.target.value })}
            />
          </label>
          <div className="c-validate-field">
            <span>Who plays the customer?</span>
            <div
              className="c-validate-segments"
              role="radiogroup"
              aria-label="Who plays the customer?"
            >
              {(
                [
                  [false, 'AI customer'],
                  [true, 'Script'],
                ] as const
              ).map(([scripted, label]) => (
                <button
                  key={label}
                  type="button"
                  role="radio"
                  aria-checked={scriptMode === scripted}
                  className={scriptMode === scripted ? 'is-active' : ''}
                  test-id={`bobcat-validate-case-mode-${scripted ? 'script' : 'ai'}`}
                  onClick={() => {
                    setScriptMode(scripted);
                    if (!scripted) set({ script: undefined });
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <label className="c-validate-field">
            <span>
              {scriptMode
                ? 'What it tests'
                : 'What happens in the conversation?'}
            </span>
            <textarea
              rows={scriptMode ? 2 : 3}
              value={value.description}
              placeholder={
                scriptMode
                  ? 'e.g. Tracking an order from the main menu — for the judge, and for ai: steps'
                  : 'e.g. A customer asks to update their shipping address, but gives an incomplete street name'
              }
              test-id="bobcat-validate-case-description"
              onChange={(e) => set({ description: e.target.value })}
            />
          </label>
          {scriptMode && (
            <label className="c-validate-field">
              <span>Script · one step per line</span>
              <textarea
                rows={6}
                className="c-validate-script"
                value={value.script ?? ''}
                placeholder={[
                  'Hi',
                  'click:Track my order',
                  'expect:order number',
                  '12345',
                  'ai:Ask when it will arrive',
                ].join('\n')}
                spellCheck={false}
                test-id="bobcat-validate-case-script"
                onChange={(e) => set({ script: e.target.value || undefined })}
              />
              <small>
                A line is sent as typed. <code>click:</code> taps a button,{' '}
                <code>menu:</code> picks from the persistent menu,{' '}
                <code>ai:</code> lets the AI customer pursue a goal,{' '}
                <code>expect:</code> checks the last reply,{' '}
                <code>webview:submit {'{…}'}</code> / <code>webview:close</code>{' '}
                answer an open webview.
              </small>
              {parsed && parsed.errors.length > 0 && (
                <p className="c-validate-error" role="alert">
                  {parsed.errors.join(' · ')}
                </p>
              )}
              {parsed && !parsed.errors.length && parsed.steps.length > 0 && (
                <ol
                  className="c-validate-script-preview"
                  test-id="bobcat-validate-case-script-preview"
                >
                  {parsed.steps.map((step, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={i}>{describeStep(step)}</li>
                  ))}
                </ol>
              )}
            </label>
          )}
          <label className="c-validate-field">
            <span>What should the solution do?</span>
            <input
              value={value.expectation ?? ''}
              placeholder="e.g. Validate the address and confirm it back"
              test-id="bobcat-validate-case-expectation"
              onChange={(e) =>
                set({ expectation: e.target.value || undefined })
              }
            />
          </label>
          {expectedAgentEnabled && agents.length > 0 && (
            <label className="c-validate-field">
              <span>What should handle it?</span>
              <AgentSelect
                testId="bobcat-validate-case-agent"
                agents={agents}
                value={value.expectedAgent?.id ?? null}
                onChange={(id) =>
                  set({
                    expectedAgent: agents.find((a) => a.id === id) ?? null,
                  })
                }
              />
            </label>
          )}
          <div className="c-validate-field-row">
            <LinesField
              label="Replies must say"
              value={value.mustSay}
              placeholder={
                'One per line, e.g.\norder number\n/ships (today|tomorrow)/i'
              }
              testId="bobcat-validate-case-must-say"
              onChange={(mustSay) => set({ mustSay })}
            />
            <LinesField
              label="…and must never say"
              value={value.mustNotSay}
              placeholder={'e.g.\nsomething went wrong'}
              testId="bobcat-validate-case-must-not-say"
              onChange={(mustNotSay) => set({ mustNotSay })}
            />
          </div>
          {!scriptMode && (
            <div className="c-validate-field">
              <span>Where does it start?</span>
              <StartSegments
                value={value.startAt}
                onChange={(startAt) => set({ startAt })}
                testIdPrefix="bobcat-validate-case-start"
              />
              <small>{start?.hint}</small>
            </div>
          )}
          <label className="c-validate-field c-validate-field--inline">
            <span>
              {scriptMode
                ? 'AI customer messages per ai: step, at most'
                : 'Customer messages, at most'}
            </span>
            <input
              type="number"
              min={1}
              max={MAX_TURNS_LIMIT}
              value={value.maxTurns}
              test-id="bobcat-validate-case-max-turns"
              onChange={(e) => set({ maxTurns: clampMaxTurns(e.target.value) })}
            />
          </label>
        </div>
      )}
    </div>
  );
};

export const SuitePanel: FC<{
  suite: ValidateSuite;
  isNew: boolean;
  channels: RunChannel[];
  agents: ExpectedAgent[];
  makeId: () => string;
  onSave: (suite: ValidateSuite) => void;
  onClose: () => void;
}> = ({ suite, isNew, channels, agents, makeId, onSave, onClose }) => {
  const [draft, setDraft] = useState<ValidateSuite>(suite);
  const [openCase, setOpenCase] = useState<string | null>(null);
  const [csvNote, setCsvNote] = useState<string | null>(null);
  const csvInput = useRef<HTMLInputElement>(null);
  useEffect(() => setDraft(suite), [suite]);
  const setContext = (patch: Partial<SuiteContext>) =>
    setDraft((d) => {
      const context = { ...d.context, ...patch };
      (Object.keys(context) as (keyof SuiteContext)[]).forEach((k) => {
        if (!context[k]?.trim()) delete context[k];
      });
      return {
        ...d,
        context: Object.keys(context).length ? context : undefined,
      };
    });
  const contextField = (
    key: keyof SuiteContext,
    label: string,
    placeholder: string
  ) => (
    <label className="c-validate-field">
      <span>{label}</span>
      <input
        value={draft.context?.[key] ?? ''}
        placeholder={placeholder}
        test-id={`bobcat-validate-suite-context-${key}`}
        onChange={(e) => setContext({ [key]: e.target.value })}
      />
    </label>
  );

  const set = (patch: Partial<ValidateSuite>) =>
    setDraft((d) => ({ ...d, ...patch }));
  const setCase = (id: string, next: SuiteCase) =>
    setDraft((d) => ({
      ...d,
      cases: d.cases.map((c) => (c.id === id ? next : c)),
    }));

  return (
    <div className="c-validate-panel" test-id="bobcat-validate-suite-panel">
      <PanelHead
        title={isNew ? 'New suite' : 'Edit suite'}
        icon="duplicate"
        onClose={onClose}
      />
      <div className="c-validate-panel__scroll">
        <label className="c-validate-field">
          <span>Suite name</span>
          <input
            value={draft.name}
            test-id="bobcat-validate-suite-name"
            onChange={(e) => set({ name: e.target.value })}
          />
        </label>
        <label className="c-validate-field">
          <span>Channel</span>
          {channels.length ? (
            <ChannelSelect
              testId="bobcat-validate-suite-channel"
              channels={channels}
              value={draft.channelAppId}
              onChange={(appId) => set({ channelAppId: appId })}
            />
          ) : (
            <TextCaption>This solution has no channel to run on.</TextCaption>
          )}
        </label>
        <div className="c-validate-field-row">
          <label className="c-validate-field c-validate-field--inline">
            <span>Runs per case</span>
            <input
              type="number"
              min={1}
              max={MAX_ITERATIONS}
              value={draft.iterations}
              test-id="bobcat-validate-suite-iterations"
              onChange={(e) =>
                set({ iterations: clampIterations(e.target.value) })
              }
            />
          </label>
          <label className="c-validate-field c-validate-field--inline">
            <span>At a time</span>
            <input
              type="number"
              min={1}
              max={MAX_CONCURRENCY}
              value={draft.concurrency}
              test-id="bobcat-validate-suite-concurrency"
              onChange={(e) =>
                set({ concurrency: clampConcurrency(e.target.value) })
              }
            />
          </label>
        </div>

        <details
          className="c-validate-suite-more"
          open={!!draft.context}
          test-id="bobcat-validate-suite-context"
        >
          <summary>The customer’s page</summary>
          <small>
            What every conversation reports, the way a customer’s page would.
            The page URL carries its query parameters to the flow.
          </small>
          {contextField(
            'pageUrl',
            'Page URL',
            'https://www.example.com/help?plan=gold&ref=email'
          )}
          <label className="c-validate-field">
            <span>Passthrough</span>
            <textarea
              rows={2}
              value={draft.context?.passthrough ?? ''}
              placeholder={'JSON, e.g. {"tier":"gold"}, or plain text'}
              spellCheck={false}
              test-id="bobcat-validate-suite-context-passthrough"
              onChange={(e) => setContext({ passthrough: e.target.value })}
            />
          </label>
          <div className="c-validate-field-row">
            {contextField('firstName', 'First name', 'Ada')}
            {contextField('lastName', 'Last name', 'Lovelace')}
          </div>
          <div className="c-validate-field-row">
            {contextField('email', 'Email', 'ada@example.com')}
            {contextField('phone', 'Phone', '+1 555 0100')}
          </div>
        </details>

        <details
          className="c-validate-suite-more"
          open={!!draft.models?.length}
          test-id="bobcat-validate-suite-compare"
        >
          <summary>Compare models</summary>
          <small>
            Runs every case once per model, with the solution’s AI turns on that
            model through the AI Gateway (<code>x-ai-eg-model</code>). Up to{' '}
            {MAX_MODELS}. Leave empty to use the solution’s own.
          </small>
          <LinesField
            label="Models, one per line"
            value={draft.models}
            placeholder={'e.g.\ngemini-2.5-flash\nopenai/gpt-4.1-mini'}
            testId="bobcat-validate-suite-models"
            rows={3}
            onChange={(models) => set({ models })}
          />
        </details>

        <div className="c-validate-cases">
          <div className="c-validate-cases__bar">
            <span className="c-validate-cases__label">
              Cases · {draft.cases.length}
            </span>
            <input
              ref={csvInput}
              type="file"
              accept=".csv,text/csv"
              hidden
              test-id="bobcat-validate-suite-csv-file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                void file.text().then((text) => {
                  try {
                    const { cases, note } = casesFromCsv(text, makeId);
                    set({ cases: [...draft.cases, ...cases] });
                    setCsvNote(`Added ${note}.`);
                  } catch (err) {
                    setCsvNote(
                      err instanceof Error ? err.message : String(err)
                    );
                  }
                });
              }}
            />
            <button
              type="button"
              className="c-validate-link"
              test-id="bobcat-validate-suite-csv-import"
              onClick={() => {
                setCsvNote(null);
                csvInput.current?.click();
              }}
            >
              Add from CSV
            </button>
            <button
              type="button"
              className="c-validate-link"
              test-id="bobcat-validate-suite-csv-export"
              disabled={!draft.cases.length}
              onClick={() =>
                download(
                  `${fileStem(draft.name)}-cases.csv`,
                  casesToCsv(draft.cases),
                  'text/csv'
                )
              }
            >
              Export CSV
            </button>
            <button
              type="button"
              className="c-validate-link"
              test-id="bobcat-validate-suite-json-export"
              onClick={() =>
                download(
                  `${fileStem(draft.name)}.json`,
                  suiteToJson(draft),
                  'application/json'
                )
              }
            >
              Export suite
            </button>
          </div>
          {csvNote && <TextCaption>{csvNote}</TextCaption>}
          {draft.cases.map((c, i) => (
            <CaseEditor
              key={c.id}
              index={i}
              value={c}
              agents={agents}
              open={openCase === c.id}
              onToggle={() => setOpenCase(openCase === c.id ? null : c.id)}
              onChange={(next) => setCase(c.id, next)}
              onRemove={() =>
                set({ cases: draft.cases.filter((x) => x.id !== c.id) })
              }
            />
          ))}
          <Button
            size="small"
            variant="ghost"
            test-id="bobcat-validate-suite-add-case"
            onClick={() => {
              const c = newCase(makeId());
              set({ cases: [...draft.cases, c] });
              setOpenCase(c.id);
            }}
          >
            <Icon name="add" slot="prefix" size="xsmall" />
            Add case
          </Button>
        </div>
      </div>
      <div className="c-validate-panel__actions">
        <Button
          size="small"
          variant="primary"
          test-id="bobcat-validate-suite-save"
          disabled={!draft.name.trim()}
          onClick={() => onSave({ ...draft, name: draft.name.trim() })}
        >
          Save suite
        </Button>
        <Button size="small" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

/* ---- Save as suite case ------------------------------------------------- */

export const SaveCasePanel: FC<{
  run: ValidateRun;
  suites: ValidateSuite[];
  onSave: (target: { suiteId: string } | { newSuiteName: string }) => void;
  onClose: () => void;
}> = ({ run, suites, onSave, onClose }) => {
  const [target, setTarget] = useState<string>(suites[0]?.id ?? 'new');
  const [newName, setNewName] = useState('New suite');
  return (
    <div className="c-validate-panel" test-id="bobcat-validate-save-case-panel">
      <PanelHead
        title="Save as suite case"
        icon="duplicate"
        onClose={onClose}
      />
      <div className="c-validate-panel__scroll">
        <div className="c-validate-save-case__run">
          <b>{run.title}</b>
          {run.expectation && <span>Expected: {run.expectation}</span>}
        </div>
        <div className="c-validate-field">
          <span>Add to</span>
          <div className="c-validate-save-case__targets" role="radiogroup">
            {suites.map((s) => (
              <label key={s.id}>
                <input
                  type="radio"
                  name="save-case-target"
                  checked={target === s.id}
                  onChange={() => setTarget(s.id)}
                />
                {s.name}
                <small>
                  {s.cases.length} {s.cases.length === 1 ? 'case' : 'cases'}
                </small>
              </label>
            ))}
            <label>
              <input
                type="radio"
                name="save-case-target"
                checked={target === 'new'}
                test-id="bobcat-validate-save-case-new"
                onChange={() => setTarget('new')}
              />
              A new suite
            </label>
          </div>
        </div>
        {target === 'new' && (
          <label className="c-validate-field">
            <span>New suite name</span>
            <input
              value={newName}
              test-id="bobcat-validate-save-case-name"
              onChange={(e) => setNewName(e.target.value)}
            />
          </label>
        )}
      </div>
      <div className="c-validate-panel__actions">
        <Button
          size="small"
          variant="primary"
          test-id="bobcat-validate-save-case"
          disabled={target === 'new' && !newName.trim()}
          onClick={() =>
            onSave(
              target === 'new'
                ? { newSuiteName: newName.trim() }
                : { suiteId: target }
            )
          }
        >
          Save case
        </Button>
        <Button size="small" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
